<template>
  <div class="workpackage-info">
    <!-- Workpackage info section -->
    <div class="section-container mb-6">
      <div class="info-title mb-4">{{ $t('workPackages.workpackageInfo') }}</div>

      <!-- Name field -->
      <div class="mb-4">
        <div class="info-label mb-1">{{ $t('workPackages.name') }}</div>
        <div
          class="info-value-box description-value d-flex justify-space-between align-center"
          @click="selectWorkpackage()"
        >
          <div>{{ workpackage.description }}</div>
          <v-icon small color="#023B6E">mdi-chevron-right</v-icon>
        </div>
      </div>

      <!-- Store group field -->
      <div class="mb-4">
        <div class="info-label mb-1">{{ $t('workPackages.sourceWorkpackage') }}</div>
        <div class="info-value-box">
          <div>{{ storeGroup }}</div>
        </div>
      </div>

      <!-- Completion and Go live dates -->
      <div>
        <div class="d-flex">
          <div class="flex-grow-1 mr-2">
            <div class="info-label mb-1">{{ $t('workPackages.completionDate') }}</div>
            <div class="info-value-box d-flex align-center">
              <v-icon small color="#023B6E" class="mr-1">mdi-calendar</v-icon>
              <span>{{ formatLocalisedDate(workpackage.completionDate) }}</span>
            </div>
          </div>
          <div class="flex-grow-1 ml-2">
            <div class="info-label mb-1">{{ $t('workPackages.goLiveDate') }}</div>
            <div class="info-value-box d-flex align-center">
              <v-icon small color="#023B6E" class="mr-1">mdi-calendar</v-icon>
              <span>{{ formatLocalisedDate(workpackage.goLiveDate) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="workpackage.lastUpdated" class="section-container">
      <div class="d-flex align-center mb-4">
        <div class="last-updated mr-2">{{ $t('workPackages.lastUpdated') }}</div>
        <div class="refresh-info d-flex">
          <feature-toggle v-if="!isMasterWorkpackage" toggle="refreshWorkpackageFromDatabricks">
            <progress-spinner
              v-if="refreshing"
              :job-id="workpackage.description"
              job-type="run_refresh_workpackage"
            />
            <v-btn
              v-else
              x-small
              depressed
              class="refresh-btn"
              :disabled="disabled"
              @click="refreshWorkpackage({ workpackageId: workpackage._id })"
            >
              <v-icon v-once size="2rem">mdi-refresh</v-icon>
            </v-btn>
          </feature-toggle>
          <div class="refresh-text">
            <div>{{ lastUpdatedText }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { refreshWorkpackageFromDatabricks } from '@enums/feature-flags';
import { get } from 'lodash';
import { workpackageTypes } from '@enums/workpackage-types';
import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import { yearMonthDayHoursMinutesSecondsFormat } from '@enums/date-formats';
import featureFlagsMixin from '../../../mixins/featureFlags';

export default {
  name: 'WorkpackageInfo',
  mixins: [featureFlagsMixin],
  props: {
    workpackage: {
      type: Object,
      default: () => {},
    },
    storeGroup: {
      type: String,
      required: true,
    },
    selectWorkpackage: {
      type: Function,
      required: false,
    },
  },
  data() {
    return {
      workpackageTypes,
    };
  },
  computed: {
    ...mapState('workpackages', [
      'deletingIds',
      'exportingIds',
      'postingIds',
      'refreshingIds',
      'loading',
    ]),
    lastUpdatedText() {
      const lastUpdatedDate = this.getFormattedDateTime(this.workpackage.lastUpdated);
      const updatedAtLabel = this.$t('workPackages.updatedAt');
      if (
        this.isFeatureFlagEnabled(refreshWorkpackageFromDatabricks) &&
        this.workpackage.lastUpdatedBy
      ) {
        return `${updatedAtLabel} ${this.$t('workPackages.lastUpdatedBy', {
          date: lastUpdatedDate,
          username: this.workpackage.lastUpdatedBy,
        })}`;
      }
      return `${updatedAtLabel} ${lastUpdatedDate}`;
    },
    isMasterWorkpackage() {
      return get(this.workpackage, 'type') === get(workpackageTypes, 'master');
    },
    deleting() {
      return this.deletingIds.includes(this.workpackage._id);
    },
    exporting() {
      return this.exportingIds.includes(this.workpackage._id);
    },
    posting() {
      return this.postingIds.includes(this.workpackage._id);
    },
    refreshing() {
      return this.refreshingIds.includes(this.workpackage._id);
    },
    disabled() {
      return this.deleting || this.exporting || this.posting || this.refreshing;
    },
  },
  methods: {
    ...mapActions('workpackages', ['refreshWorkpackage']),
    getFormattedDateTime(datetime) {
      const localtime = moment
        .utc(datetime)
        .local()
        .format(yearMonthDayHoursMinutesSecondsFormat);
      const [date, time] = localtime.split(' ');
      const [hours, minutes] = time.split(':');
      return `${this.formatLocalisedDate(date)} ${[hours, minutes].join(':')}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.workpackage-info {
  background-color: #fff;
}

.section-container {
  margin-bottom: 24px;
}

.info-title {
  color: #343a3f;
  font-size: 1.4rem;
  font-weight: bold;
}

.last-updated {
  color: #343a3f;
  font-size: 1.2rem;
  font-weight: bold;
}

.info-label {
  font-size: 1.2rem;
  font-weight: 400;
  color: #343a3f;
  letter-spacing: 0.5px;
}

.info-value-box {
  font-size: 1.2rem;
  font-weight: 400;
  color: #697077;
  border-bottom: 2px solid #697077;
  padding: 6px 4px;
  min-height: 32px;
  display: flex;
  align-items: center;
  // For icon on left, text, and optional right icon
  .v-icon {
    margin-right: 6px;
    min-width: 18px;
  }
  // For right-aligned content (dates)
  &.justify-end {
    justify-content: flex-end;
  }
  // For space-between (store group)
  &.justify-space-between {
    justify-content: space-between;
  }
}

.refresh-info {
  gap: 8px;
  .refresh-text {
    font-size: 1.2rem;
    color: #343a3f;
    margin: auto;
  }
}

.refresh-btn {
  background-color: $pricing-primary !important;
  color: white !important;
  min-width: unset !important;
  padding: unset !important;
  height: 3rem !important;
  width: 3rem;
  .v-icon {
    color: white !important;
  }
}

.description-value {
  cursor: pointer;
}
</style>
